import {
    Edit,
    SimpleForm,
    SelectInput,
    TextInput,
    SelectArrayInput,
    ReferenceArrayInput,
    required,
    ArrayInput,
    SimpleFormIterator,
    FormDataConsumerRenderParams,
    FormDataConsumer,
    WithRecord,
    BooleanInput,
    NumberInput,
    TextField,
    useArrayInput,
    useInfiniteGetList
} from 'react-admin';
import {
    nokTypes,
    nokStatuses,
    nokTestTypes,
    nokLocations,
    nokScoringTypes,
    nokEmailLocations,
    nokCustomerDataPoints,
    nokAiModels
} from "../../defaults/defaults";
import { JSX, ReactElement, ReactNode, useState, useEffect } from "react";
import {
    Alert,
    Box,
    Button,
    Grid,
    LinearProgress,
    Stack,
    SxProps,
    Theme,
    Typography
} from '@mui/material';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { useASettingByCode } from "../global-hooks/default-system-hooks";
import { ChatFieldBridge } from "../chat-bot/components/ChatFieldBridge";
import { FormSection } from "./components/FormSection";
import { MONO } from "../dashboard/components/styles/dashboard-tokens";

/*
 * Layout for this screen is applied through each SimpleFormIterator's own `sx`
 * rather than by wrapping its inputs in Grid or Box. react-admin builds a newly
 * added item from the `source` and `defaultValue` of the iterator's direct
 * children (useGetArrayInputNewItemDefaults), so a wrapper would change the
 * shape of every item added from here, and the Operator reads that shape.
 * Keep inputs as direct children, and keep the existing fragments, which that
 * same lookup deliberately skips.
 */

/** Only this iterator's own rows are matched, so a nested iterator keeps its own layout. */
const ITERATOR_LINE: string = '& > ul > li.RaSimpleFormIterator-line';
const ITERATOR_FORM: string = '& > ul > li > .RaSimpleFormIterator-form';

/** Spans every column of the grid an item is laid out on. */
const FULL_ROW = { gridColumn: '1 / -1' };

/** Machine text (keys, patterns) is set in mono, the admin's own words in the UI face. */
const MONO_INPUT = { '& .MuiInputBase-input': { fontFamily: MONO } };

/** Two grid columns from md up, so a long value is not clipped; one column below that. */
const WIDE_CELL = { gridColumn: { xs: 'auto', md: 'span 2' } };

/** A key field: mono, and wide enough to show keys like from_address_automation_markers. */
const KEY_INPUT = { ...MONO_INPUT, ...WIDE_CELL };

const AUTO_COLUMNS: string = 'repeat(auto-fill, minmax(240px, 1fr))';

type SelectElementChoice = { id: string, name: string };

/**
 * Styles an iterator so each item's inputs sit on a grid. `card` draws each
 * item as a bordered block, for lists whose items hold several fields and
 * would otherwise run into one another.
 */
function iteratorSx(options: { card: boolean, columns?: string | Record<string, string>, rowGap?: number }): SxProps<Theme> {
    return {
        [ITERATOR_LINE]: options.card
            ? {
                alignItems: 'flex-start',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                padding: 2,
                marginBottom: 2,
            }
            : {
                alignItems: 'flex-start',
                borderBottom: 'none',
            },
        [ITERATOR_FORM]: {
            flex: 1,
            display: 'grid',
            gridTemplateColumns: options.columns ?? AUTO_COLUMNS,
            columnGap: 2,
            rowGap: options.rowGap ?? 0,
            alignItems: 'start',
        },
    };
}

/**
 * Heading for a group of fields inside the Configuration section. On a complex
 * Nok that section runs to thousands of pixels, so each group is marked by a
 * tinted band with a rail, and a list says how many items it holds.
 * `order` lets a heading sit with inputs that are themselves reordered.
 */
function SubHeading(props: { title: string, hint?: ReactNode, count?: number, order?: number }): JSX.Element {
    return (
        <Box sx={ {
            ...FULL_ROW,
            order: props.order ?? 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            margin: '8px 0 16px 0',
            padding: '10px 16px',
            backgroundColor: 'action.hover',
            borderLeft: '4px solid',
            borderLeftColor: 'primary.main',
            borderRadius: 1,
        } }>
            <Box>
                <Typography variant="h6" component="h3" sx={ { fontWeight: 600, lineHeight: 1.3 } }>
                    { props.title }
                </Typography>
                { props.hint &&
                    <Typography variant="body2" sx={ { color: 'text.secondary' } }>
                        { props.hint }
                    </Typography>
                }
            </Box>
            { props.count !== undefined &&
                <Typography variant="body2" sx={ { color: 'text.secondary', fontFamily: MONO, whiteSpace: 'nowrap' } }>
                    { props.count === 1 ? '1 item' : props.count + ' items' }
                </Typography>
            }
        </Box>
    );
}

/** A SubHeading counting the items of the ArrayInput it is rendered inside. */
function ListSubHeading(props: { title: string, hint?: ReactNode }): JSX.Element {
    const { fields } = useArrayInput();

    return <SubHeading title={ props.title } hint={ props.hint } count={ fields.length } />;
}

/** The explanation shown above the fields a choice reveals. */
function ChoiceHint(props: { children: ReactNode }): JSX.Element {
    return (
        <Typography variant="body2" sx={ { ...FULL_ROW, color: 'text.secondary', marginBottom: 1 } }>
            { props.children }
        </Typography>
    );
}

/** A value the admin can read but not edit, with its label beside it. */
function ReadOnlyValue(props: { label: string, source: string }): JSX.Element {
    return (
        <Box sx={ {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 2,
            padding: '10px 0',
            borderBottom: '1px solid',
            borderColor: 'divider',
        } }>
            <Typography variant="body2" sx={ { color: 'text.secondary' } }>
                { props.label }
            </Typography>
            <TextField source={ props.source }
                       sx={ { fontFamily: MONO, fontWeight: 600, wordBreak: 'break-all', textAlign: 'right' } } />
        </Box>
    );
}

const PaginatedSelectInput: ({ source, reference } : { source: string, reference: string }) => ReactElement = ({ source, reference } : { source: string, reference: string }): ReactElement => {
    const {
        data,
        total,
        isPending,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useInfiniteGetList(reference, {
        pagination: { page: 1, perPage: 25 },
        sort: { field: 'name', order: 'ASC' },
    });

    // Flatten all pages into a single choices array
    const allChoices = data?.pages.flatMap(page => page.data) ?? [];

    return (
        <Box>
            <SelectInput
                source={source}
                choices={ allChoices }
                isPending={ isPending }
                fullWidth
            />
            {hasNextPage && (
                <Button
                    size="small"
                    onClick={ () => fetchNextPage() }
                    disabled={isFetchingNextPage}
                >
                    {isFetchingNextPage ? 'Loading…' : `Load more (${allChoices.length}/${total})`}
                </Button>
            )}
        </Box>
    );
};


const ScoringBlock: () => ReactElement = (): ReactElement => {
    const form: UseFormReturn = useFormContext();
    const type: string = form.watch('type')

    if (type === 'simple') {
        return (
            <>
                <SubHeading title="Scoring" />
                <TextInput source="simple_score" validate={[required()]} defaultValue="Pass / Fail / Catastrophe" fullWidth readOnly />
                {/*<span>{ "If the type of question requires breakpoints" }</span>*/}
                {/*<NumberInput*/}
                {/*    source="simple_pass_threshold_score"*/}
                {/*    label="Score on pass"*/}
                {/*    helperText="Returned when the condition is met"*/}
                {/*/>*/}
                {/*<NumberInput*/}
                {/*    source="simple_fail_threshold_score"*/}
                {/*    label="Score on pass"*/}
                {/*    helperText="Returned when the condition is met"*/}
                {/*/>*/}
                {/*<NumberInput*/}
                {/*    source="simple_catastrophe_threshold_score"*/}
                {/*    label="Score on pass"*/}
                {/*    helperText="Returned when the condition is met"*/}
                {/*/>*/}
            </>
        );
    }

    return (
        <ArrayInput source="scores" label={ false } sx={ FULL_ROW }>
            <ListSubHeading title="Scores" />
            <SimpleFormIterator sx={ iteratorSx({ card: true }) }>
                <SelectInput source="scoring_type" defaultValue="" choices={ nokScoringTypes } fullWidth sx={ FULL_ROW } />
                <FormDataConsumer>
                    {({ scopedFormData }: FormDataConsumerRenderParams): JSX.Element =>
                        scopedFormData?.scoring_type === 'boolean' ? (
                            <>
                                <ChoiceHint>{ "Returns one of two scores depending on whether the evaluation passes or fails." }</ChoiceHint>
                                <TextInput source="key" validate={[required()]} sx={ KEY_INPUT } />
                                <NumberInput
                                    source="pass_score"
                                    label="Score on pass"
                                    helperText="Returned when the condition is met"
                                />
                                <NumberInput
                                    source="fail_score"
                                    label="Score on fail"
                                    helperText="Returned when the condition is not met"
                                />
                                <NumberInput
                                    source="threshold"
                                    label="Minimum count threshold (Optional)"
                                    helperText="Occurrences at or below this count score 0 (e.g. 2)"
                                />
                            </>
                        ) : scopedFormData?.scoring_type === 'scaled_boolean' ? (
                            <>
                                <ChoiceHint>{ "Returns one of three scores depending on the outcome. Define a label and score for each outcome." }</ChoiceHint>
                                <TextInput source="key" validate={[required()]} sx={ KEY_INPUT } />
                                <ArrayInput source="outcomes" label="" sx={ FULL_ROW }>
                                    <SimpleFormIterator
                                        sx={ iteratorSx({ card: false }) } inline disableReordering>
                                        <TextInput source="label" label="Outcome label" helperText="e.g. verified_match"  />
                                        <NumberInput source="score" label="Score" />
                                    </SimpleFormIterator>
                                </ArrayInput>
                            </>
                        ) : scopedFormData?.scoring_type === 'banded' ? (
                            <>
                                <ChoiceHint>{ "Define contiguous value ranges. Each band covers from the previous max up to its own max. Leave max empty on the final band to cover all remaining values." }</ChoiceHint>
                                <TextInput source="key" validate={[required()]} sx={ KEY_INPUT } />
                                <NumberInput
                                    source="optional_empty_score"
                                    label="Default score if applicable to scoring"
                                    helperText="Returned when the condition is not met"
                                />
                                <ArrayInput source="bands" label="" sx={ FULL_ROW }>
                                    <SimpleFormIterator
                                        sx={ iteratorSx({ card: false }) } inline>
                                        <NumberInput source="max"   label="Max value (empty = final band)" helperText="Upper boundary of this band" />
                                        <NumberInput source="score" label="Score" helperText="Score returned for this band"  />
                                    </SimpleFormIterator>
                                </ArrayInput>
                            </>
                        ) : scopedFormData?.scoring_type === 'accumulated' ? (
                            <>
                                <ChoiceHint>{ "Score increases with each occurrence found. Optionally cap the maximum total score." }</ChoiceHint>
                                <TextInput source="key" validate={[required()]} sx={ KEY_INPUT } />
                                <NumberInput
                                    source="per_occurrence"
                                    label="Score per occurrence"
                                    helperText="Added to total for each occurrence found"
                                />
                                <NumberInput
                                    source="on_zero"
                                    label="Score when count is zero"
                                    helperText="Returned when nothing is found (e.g. 0 or a negative)"
                                />
                                <NumberInput
                                    source="maximum"
                                    label="Maximum score (empty = no cap)"
                                    helperText="Total score will not exceed this value"
                                />
                            </>
                        )  : scopedFormData?.scoring_type === 'threshold_accumulated' ? (
                            <>
                                <ChoiceHint>{ "The first N occurrences score nothing. Only occurrences above the threshold accumulate a score." }</ChoiceHint>
                                <TextInput source="key" validate={[required()]} sx={ KEY_INPUT } />
                                <NumberInput
                                    source="threshold"
                                    label="Minimum count threshold"
                                    helperText="Occurrences at or below this count score 0 (e.g. 2)"
                                />
                                <NumberInput
                                    source="per_occurrence"
                                    label="Score per occurrence above threshold"
                                    helperText="Added for each occurrence above the threshold"
                                />
                                <NumberInput
                                    source="on_zero"
                                    label="Score when count is zero"
                                    helperText="Returned when nothing is found (e.g. 0 or a negative)"
                                />
                                <NumberInput
                                    source="on_below_threshold"
                                    label="Score below threshold"
                                    helperText="Returned when count is above zero but at or below the threshold. Leave empty to use the on_zero value"
                                />
                                {/*<NumberInput*/}
                                {/*    source="maximum"*/}
                                {/*    label="Maximum score (empty = no cap)"*/}
                                {/*    helperText="Total score will not exceed this value"*/}
                                {/*/>*/}
                            </>
                        ) : scopedFormData?.scoring_type === 'single_value' ? (
                            <>
                                <ChoiceHint>{ "For when a single score value is needed" }</ChoiceHint>
                                <TextInput source="key" validate={[required()]} sx={ KEY_INPUT } />
                                <NumberInput
                                    source="score"
                                    label="Single Score"
                                />
                            </>
                        ) : null
                    }
                </FormDataConsumer>
            </SimpleFormIterator>
        </ArrayInput>
    );
}


/**
 * The header picker a `headers` location reveals. Rendered inside both the
 * simple and the complex location lists, against whichever item is in scope.
 */
function HeaderLocationInputs(props: { loadingSetting: boolean, headersList: { id: string, name: string }[], error: any }): JSX.Element {
    return (
        <FormDataConsumer>
            {({ scopedFormData }: FormDataConsumerRenderParams): JSX.Element =>
                scopedFormData?.location === 'headers' ? (
                    <>
                        { (props.loadingSetting) &&
                            <LinearProgress sx={ { ...FULL_ROW, width: '100%', marginBottom: 2 } } />
                        }
                        { (!props.loadingSetting && props.headersList.length > 0) &&
                            <SelectInput source='header'
                                         validate={[required()]}
                                         defaultValue=""
                                         choices={ props.headersList }
                                         sx={ WIDE_CELL } />
                        }
                        { (!props.loadingSetting && props.headersList.length <= 0) &&
                            <Typography variant="body2" sx={ { ...FULL_ROW, color: 'error.main' } }>{ "Something went wrong: " + props.error }</Typography>
                        }
                        <TextInput source="custom_header"
                                   helperText="If this value is empty when custom is selected, it will be ignored"
                                   fullWidth />
                    </>
                ) : null
            }
        </FormDataConsumer>
    );
}

const LocationBlock: () => ReactElement = (): ReactElement => {
    const form: UseFormReturn = useFormContext();
    const type: string = form.watch('type')
    const [headersList, setHeadersList] = useState< { id: string, name: string }[] >( [] );

    const { loadingSetting, settingValue, loadSettingValue, error } = useASettingByCode("email_headers_list");


    useEffect( (): void =>  {
        if (!loadingSetting && headersList.length === 0) {
            // Process loaded setting values into usable list
            let settingsList: { key: string, value: string }[] = [];
            if (settingValue) {
                settingsList = JSON.parse(settingValue)
            }
            let returnList: { id: string, name: string }[] = [];
            for(const header of settingsList) {
                returnList.push({ id: header.key, name: header.value });
            }
            returnList.push({ id: "custom", name: "Custom" });
            setHeadersList(returnList);
        }
    }, [loadingSetting] );

    return (
        <ArrayInput source="locations" label={ false } sx={ FULL_ROW }>
            <ListSubHeading title="Locations" />
            <SimpleFormIterator
                disableReordering
                sx={ iteratorSx({ card: true }) }>
                { (type === 'complex') &&
                    <TextInput source="function_key" label="Nok Functional Key" validate={[required()]} sx={ KEY_INPUT } />
                }
                <SelectInput source='which_email' choices={ nokEmailLocations } validate={[required()]} />
                { (type === 'simple') &&
                    <>
                        <SelectInput source="location" choices={ nokLocations } validate={[required()]} />
                        <HeaderLocationInputs loadingSetting={ loadingSetting } headersList={ headersList } error={ error } />
                    </>
                }
                { (type === 'complex') &&
                    <>
                        <ArrayInput source="locations" sx={ FULL_ROW }>
                            <SimpleFormIterator sx={ iteratorSx({ card: false }) }>
                                <SelectInput source="location" choices={ nokLocations } validate={[required()]} />
                                <HeaderLocationInputs loadingSetting={ loadingSetting } headersList={ headersList } error={ error } />
                            </SimpleFormIterator>
                        </ArrayInput>

                    </>
                }

            </SimpleFormIterator>
        </ArrayInput>
    );
}

const ComplexPromptBlock: () => ReactElement = (): ReactElement => {
    const form: UseFormReturn = useFormContext();
    const type: string = form.watch('type')

    if (type === 'simple') {
        return <></>
    }

    return (
        <ArrayInput source="prompts" label={ false } sx={ FULL_ROW }>
            <ListSubHeading title="Prompts" />
            <SimpleFormIterator sx={ iteratorSx({ card: true, columns: '1fr 1fr' }) }>
                <TextInput source="key" validate={[required()]} sx={ MONO_INPUT } />
                <SelectInput source="llm_model"
                             validate={[required()]}
                             choices={ nokAiModels } />
                <TextInput source="system_prompt"
                           multiline
                           rows={ 5 }
                           validate={[required()]}
                           helperText="What does the agent do?"
                           fullWidth
                           sx={ FULL_ROW } />
                <TextInput source="action_prompt"
                           multiline
                           rows={ 5 }
                           // validate={[required()]}
                           helperText="Give the Agent some instructions"
                           fullWidth
                           sx={ FULL_ROW } />
                <BooleanInput source="disable_sanitation"
                    // label=""
                              helperText="If enabled, any email data sent to the agent won't be sanitized (emails and domains replaced with placeholders)"
                              sx={ FULL_ROW }
                />
            </SimpleFormIterator>
        </ArrayInput>
    );
}



const SimpleScoresLabelsBlock: () => ReactElement = (): ReactElement => {
    const form: UseFormReturn = useFormContext();
    const type: string = form.watch('type')
    const complexBands: any = form.watch('complexScoreBands');


    if (type === 'complex') {
        return(
            <FormSection title="Score range">
                <Grid container columnSpacing={ 2 }>
                    <Grid size={ { xs: 12, md: 4 } }>
                        <NumberInput source="minimumScore"
                                     helperText="Lowest score for question"
                                     validate={[required()]}
                                     defaultValue={ 0 }
                                     fullWidth />
                    </Grid>
                    <Grid size={ { xs: 12, md: 4 } }>
                        <NumberInput source="maximumScore"
                                     helperText="Maximum score for question"
                                     validate={[required()]}
                                     defaultValue={ 100 }
                                     fullWidth />
                    </Grid>
                    <Grid size={ { xs: 12, md: 4 } }>
                        <NumberInput source="startingScore"
                                     helperText="Set a starting score if not starting from 0"
                                     validate={[required()]}
                                     defaultValue={ 0 }
                                     fullWidth />
                    </Grid>
                </Grid>
                <ArrayInput source="complexScoreBands">
                    <SimpleFormIterator disableAdd={ ( complexBands?.length >= 5 ) }
                                        sx={ iteratorSx({ card: false, columns: { xs: '1fr', md: '1fr 2fr 1fr' } }) } >
                        <TextInput source="bandLabelShort" validate={[required()]} fullWidth />
                        <TextInput source="bandLabelLong" validate={[required()]} fullWidth />
                        <SelectInput source="bandRange" validate={[required()]} fullWidth choices={ [
                            { id: '0_20', name: '0 - 20' },
                            { id: '21_40', name: '21 - 40' },
                            { id: '41_60', name: '41 - 60' },
                            { id: '61_80', name: '61 - 80' },
                            { id: '81_100', name: '81 - 100' }
                        ] } />
                    </SimpleFormIterator>
                </ArrayInput>
            </FormSection>
        );
    }

    return (
        <FormSection title="Outcome labels">
            <Grid container columnSpacing={ 2 }>
                <Grid size={ { xs: 12, md: 4 } }>
                    <TextInput source="simplePassLabel"
                               validate={[required()]}
                               fullWidth />
                </Grid>
                <Grid size={ { xs: 12, md: 8 } }>
                    <TextInput source="simplePassLabelLong"
                               validate={[required()]}
                               fullWidth />
                </Grid>
                <Grid size={ { xs: 12, md: 4 } }>
                    <TextInput source="simpleFailLabel"
                               validate={[required()]}
                               fullWidth />
                </Grid>
                <Grid size={ { xs: 12, md: 8 } }>
                    <TextInput source="simpleFailLabelLong"
                               validate={[required()]}
                               fullWidth />
                </Grid>
                <Grid size={ { xs: 12, md: 4 } }>
                    <TextInput source="simpleCatastropheLabel"
                               validate={[required()]}
                               fullWidth />
                </Grid>
                <Grid size={ { xs: 12, md: 8 } }>
                    <TextInput source="simpleCatastropheLabelLong"
                               validate={[required()]}
                               fullWidth />
                </Grid>
            </Grid>
        </FormSection>
    );
}


const ConfigurationBlock: () => ReactElement = (): ReactElement => {
    const form: UseFormReturn = useFormContext();
    const type: string = form.watch('type')
    const [defaultRegexList, setDefaultRegexList] = useState< { id: string, name: string }[] >( [] );
    const configuration: any[] = form.watch('configuration');
    const { loadingSetting: loadingDefaultRegexSetting, settingValue: defaultRegexSettingValue, loadSettingValue: loadDefaultRegexSettingValue, error: defaultRegexSettingError } = useASettingByCode("default_nok_regex_Patterns");
    useEffect( (): void =>  {
        if (!loadingDefaultRegexSetting && defaultRegexList.length === 0) {
            // Process loaded setting values into usable list
            let settingsList: { key: string, value: string }[] = [];
            if (defaultRegexSettingValue) {
                settingsList = JSON.parse(defaultRegexSettingValue)
            }
            let returnList: { id: string, name: string }[] = [];
            for(const header of settingsList) {
                returnList.push({ id: header.value, name: header.key });
            }
            setDefaultRegexList(returnList);
        }
    }, [loadingDefaultRegexSetting] );

    if (type === 'complex') {
        return (
            <>
                <ArrayInput source="configuration" label={ false }>
                    <SimpleFormIterator disableAdd={ configuration?.length >= 1 }
                                        sx={ iteratorSx({ card: false, columns: '1fr', rowGap: 4 }) }>

                        <ArrayInput source="datasets" label={ false } sx={ FULL_ROW }>
                            <ListSubHeading title="Datasets" hint="Add single or comma separated values" />
                            <SimpleFormIterator sx={ iteratorSx({ card: true, columns: { xs: '1fr', md: 'minmax(220px, 1fr) 3fr' } }) }>
                                <TextInput source="key" validate={[required()]} fullWidth sx={ MONO_INPUT } />
                                <TextInput source="value"
                                           multiline
                                           validate={[required()]}
                                           rows={ 3 }
                                           helperText={ false }
                                           fullWidth
                                           sx={ MONO_INPUT } />
                            </SimpleFormIterator>
                        </ArrayInput>
                        <ScoringBlock />
                        <LocationBlock  />
                        <ComplexPromptBlock />
                    </SimpleFormIterator>
                </ArrayInput>

            </>
        )
    }
    return (
        <ArrayInput source="configuration" label={ false }>
            <SimpleFormIterator
                disableAdd={ configuration?.length >= 1 }
                sx={ iteratorSx({ card: false, columns: { xs: '1fr', md: '1fr 1fr' }, rowGap: 2 }) }
            >

                <SubHeading title="Processing" order={ -1 } />
                <SelectInput source="processing_type"
                             validate={[required()]}
                             fullWidth
                             choices={ nokTestTypes }
                             sx={ { order: -1 } } />
                <FormDataConsumer>
                    {({ scopedFormData }: FormDataConsumerRenderParams): JSX.Element =>
                        scopedFormData?.processing_type === 'regex' ? (
                            <>
                                <ArrayInput source="regex" sx={ FULL_ROW }>
                                    <SimpleFormIterator sx={ iteratorSx({ card: true, columns: '1fr' }) }>
                                        <BooleanInput source="use_system_regex"
                                            // label="Check Start"
                                                      helperText="If enabled, a system regex pattern is used instead of a custom one"
                                        />
                                        <TextInput source="regex_key" validate={[required()]} helperText="This value is used to determine the correct regex to use in code" fullWidth sx={ MONO_INPUT } />
                                        <FormDataConsumer>
                                            {({ scopedFormData }: FormDataConsumerRenderParams): JSX.Element =>
                                                scopedFormData?.use_system_regex  ? (
                                                    <>
                                                        <SelectInput source="system_regex_value" choices={ defaultRegexList } fullWidth />
                                                    </>
                                                ) : (
                                                    <>
                                                        <TextInput source="regex_value"
                                                                   multiline
                                                                   rows={ 3 }
                                                                   helperText="Leave empty to use the default regex pattern"
                                                                   fullWidth
                                                                   sx={ MONO_INPUT } />
                                                    </>
                                                )
                                            }
                                        </FormDataConsumer>
                                    </SimpleFormIterator>
                                </ArrayInput>
                            </>
                        ) : scopedFormData?.processing_type === 'time' ? (
                            <>
                                <BooleanInput source="process_office_hours_only"
                                    // label="Check Start"
                                              helperText="If enabled, time outside of office hours won't be included"
                                              sx={ FULL_ROW }
                                />

                            </>
                        ) : scopedFormData?.processing_type === 'validate' ? (
                            <>
                                <BooleanInput source="use_dns_fallback"
                                              // label=""
                                              helperText="If applicable, DNS fallback will be used to validate if the header fails"
                                              sx={ FULL_ROW }
                                />

                            </>
                        ) : scopedFormData?.processing_type === 'ask_ai' ? (
                            <>
                                <ChoiceHint>{ "An agent will be created using the prompts to examine the email. All location data will be converted to a String (or JSON if applicable) and appended to the Agent in the Action prompt" }</ChoiceHint>
                                <SelectInput source="llm_model"
                                             validate={[required()]}
                                             choices={ nokAiModels }
                                             fullWidth />
                                <SelectInput source="expected_output"
                                             validate={[required()]}
                                             fullWidth
                                             choices={ [
                                                 { id: 'text', name: "Text" },
                                                 { id: 'bool', name: 'True/False'},
                                                 { id: 'number', name: 'Number'},
                                             ] } />
                                <TextInput source="system_prompt"
                                           multiline
                                           rows={ 5 }
                                           validate={[required()]}
                                           helperText="What does the agent do?"
                                           fullWidth
                                           sx={ FULL_ROW } />
                                <TextInput source="action_prompt"
                                           multiline
                                           rows={ 5 }
                                           validate={[required()]}
                                           helperText="Give the Agent some instructions"
                                           fullWidth
                                           sx={ FULL_ROW } />
                                <NumberInput source="seed"
                                           helperText="Seed data to be used by the agent. This is useful for testing and debugging and aiding in response consistency"
                                           fullWidth />
                                <TextInput source="model_slug"
                                           helperText="This is the model date, e.g gpt-5-mini-2025-08-07, this will append the 2025-08-07 to the model."
                                           fullWidth
                                           sx={ MONO_INPUT } />
                                <BooleanInput source="disable_sanitation"
                                    // label=""
                                              helperText="If enabled, any email data sent to the agent won't be sanitized (emails and domains replaced with placeholders)"
                                              sx={ FULL_ROW }
                                />
                            </>
                        ) : null
                    }
                </FormDataConsumer>
                <SelectInput
                    source="system_data_point"
                    label="Data Point"
                    choices={ nokCustomerDataPoints }
                    helperText="Select the data point to check for value. If unavailable, the customer will be prompted to provide the value"
                    fullWidth
                    sx={ { order: -1 } }
                />

                <LocationBlock  />
                <ScoringBlock />

            </SimpleFormIterator>
        </ArrayInput>
    );
}

/*
 * The two lists differ: the before list also offers 'more'. Kept as they were,
 * since the stored condition id is what the Operator reads.
 */
const beforeNokConditions: SelectElementChoice[] = [
    { id: 'greater', name: 'Greater Than' },
    { id: 'less', name: 'Less Than' },
    { id: 'more', name: 'More Than' },
    { id: 'equal', name: 'Equal To' },
    { id: 'equal_greater', name: 'Equal or Greater Than' },
    { id: 'equal_less', name: 'Equal or Less Than' },
];

const afterNokConditions: SelectElementChoice[] = [
    { id: 'greater', name: 'Greater Than' },
    { id: 'less', name: 'Less Than' },
    { id: 'equal', name: 'Equal To' },
    { id: 'equal_greater', name: 'Equal or Greater Than' },
    { id: 'equal_less', name: 'Equal or Less Than' },
];

/** One of the two lists of Noks run either side of this one, each with its score mapping. */
function NokExecutionInput(props: { source: string, helperText: string, conditions: SelectElementChoice[] }): JSX.Element {
    return (
        <ArrayInput source={ props.source } helperText={ props.helperText }>
            <SimpleFormIterator sx={ iteratorSx({ card: true, columns: '1fr' }) }>
                <PaginatedSelectInput source="nok" reference="noks"/>
                <ArrayInput source="scoring">
                    <SimpleFormIterator sx={ iteratorSx({ card: false, columns: 'repeat(auto-fill, minmax(160px, 1fr))' }) }>
                        <NumberInput source="input_score" validate={[required()]}/>
                        <SelectInput source="condition" choices={ props.conditions } />
                        <NumberInput source="output_score" validate={[required()]}/>
                        <NumberInput source="fail_score" validate={[required()]} defaultValue={ 0 }/>
                    </SimpleFormIterator>
                </ArrayInput>
            </SimpleFormIterator>
        </ArrayInput>
    );
}

export const NokEdit: () => JSX.Element = (): JSX.Element => {
    const { loadingSetting, settingValue, error } = useASettingByCode("default_nok_tags");

    function processSettingValue(): { id: string, name: string }[] {
        let values: { id: string, name: string }[] = [];
        if (!loadingSetting && settingValue?.length > 0) {
            const settingData: { key: string, value: string }[] = JSON.parse(settingValue);
            for (const tag of settingData) {
                values.push({ id: tag.key, name: tag.value });
            }
        }
        return values;
    }

    return (
        <Edit redirect={ false }>
            <SimpleForm >
                <Stack spacing={ 5 } sx={ { width: '100%' } }>
                    <FormSection title="Details">
                        <Grid container columnSpacing={ 4 }>
                            <Grid size={ { xs: 12, md: 7 } }>
                                <TextInput source="name"
                                           validate={[required()]}
                                           fullWidth/>
                                <TextInput source="description"
                                           multiline
                                           fullWidth
                                           rows={ 4 }
                                           validate={[required()]}
                                           helperText="This text is displayed to the customer"/>
                                { (loadingSetting) &&
                                    <LinearProgress />
                                }
                                { (!loadingSetting) &&
                                    <SelectArrayInput source="tags" choices={ processSettingValue() } fullWidth />
                                }
                            </Grid>
                            <Grid size={ { xs: 12, md: 5 } }>
                                <SelectInput source="status"
                                             choices={ nokStatuses }
                                             defaultValue="testing"
                                             fullWidth
                                             validate={[required()]}
                                             helperText="Only Active Noks are shown to customers."
                                />
                                <SelectInput source="type"
                                             choices={ nokTypes }
                                             defaultValue="simple"
                                             helperText="This value switches scoring between Pass/Fail/Catastrophe and a score range."
                                             fullWidth
                                             validate={[required()]}/>
                                <WithRecord render={
                                    (record: any): ReactElement => {
                                        if (record.operatorId) {
                                            return (
                                                <ReadOnlyValue label="Operator ID" source="operatorId" />
                                            );
                                        } else {
                                            return (
                                                <TextInput source="operatorId"
                                                           label="Operator ID"
                                                           helperText="This ID is used to identify the Operator process needed to be run. Once saved, this is uneditable"
                                                           validate={[required()]}
                                                           fullWidth/>
                                            );
                                        }
                                    }
                                } />
                                <ReadOnlyValue label="System Code" source="systemCode" />
                            </Grid>
                        </Grid>
                    </FormSection>

                    <SimpleScoresLabelsBlock />

                    <FormSection title="Minis and run order">
                        <ReferenceArrayInput source="activeMinis"
                                             filter={ { nokProcessingType: 'active' } }
                                             reference="nok_minis">
                            <SelectArrayInput validate={[required()]} fullWidth />
                        </ReferenceArrayInput>
                        <Grid container columnSpacing={ 4 }>
                            <Grid size={ { xs: 12, md: 6 } }>
                                <NokExecutionInput source="beforeNokExecution"
                                                   helperText="These Noks will run before the current Nok"
                                                   conditions={ beforeNokConditions } />
                            </Grid>
                            <Grid size={ { xs: 12, md: 6 } }>
                                <NokExecutionInput source="afterNokExecution"
                                                   helperText="These Noks will run after the current Nok"
                                                   conditions={ afterNokConditions } />
                            </Grid>
                        </Grid>
                    </FormSection>

                    <FormSection title="Configuration">
                        <Alert severity="warning" sx={ { marginBottom: 3 } }>
                            { "Be careful changing configuration values. They affect how the data is processed." }
                        </Alert>
                        <ConfigurationBlock  />
                    </FormSection>
                </Stack>

                {/* Offers this form to the assistant. Renders nothing, writes nothing by itself. */}
                <ChatFieldBridge />
            </SimpleForm>
        </Edit>
    );
}
